import type { ButtonSize } from '@timelyapp/react';
import { useButtonClassNames } from '@timelyapp/react';
import classNames from 'classnames';
import ProjectSelect from 'components/ProjectSelect';
import Tooltip from 'components/Tooltip';
import React, { useCallback } from 'react';
import FontAwesome from 'react-fontawesome';
import type { HandleThunkActionCreator } from 'react-redux';
import type { fetchProject, searchProjects } from 'redux/modules/projects.actions';
import type { batchSelectionActions } from 'redux/modules/superreports.types';
import type { Currency } from 'types/account';
import type { Client } from 'types/client';
import type { EnrichedProject, ProjectBase } from 'types/project';
import styles from './ReportBatchUpdateProject.module.scss';

interface Props {
  allProjectsLoaded: boolean;
  clients: Array<Client>;
  currency: Currency;
  disabled?: boolean;
  fetchProject: HandleThunkActionCreator<typeof fetchProject>;
  isLoading: boolean;
  projectId: number | null;
  projects: Array<EnrichedProject>;
  setSelection: typeof batchSelectionActions.setSelection;
  searchProjects: HandleThunkActionCreator<typeof searchProjects>;
  size?: ButtonSize;
  userId: number;
}

const ReportBatchUpdateProject: React.FC<Props> = ({
  allProjectsLoaded,
  clients,
  currency,
  disabled,
  fetchProject,
  isLoading,
  projectId,
  projects,
  setSelection,
  searchProjects,
  size,
  userId,
}) => {
  const handleProjectChange = useCallback(
    async (project?: ProjectBase) => {
      setSelection({
        selectionKey: 'projectId',
        value: project?.id ?? null,
      });

      if (project?.id) {
        await fetchProject(project.id);
      }
    },
    [setSelection, fetchProject]
  );

  const handleProjectClear = useCallback(() => handleProjectChange(), [handleProjectChange]);

  const handleProjectSearch = useCallback(
    (filter: string) => searchProjects(filter, userId),
    [userId, searchProjects]
  );

  const { className: buttonClassNames } = useButtonClassNames({
    disabled,
    priority: 'medium',
    selected: projectId != null,
    size,
  });

  return (
    <div
      data-testid="update_project_dropdown"
      className={classNames(buttonClassNames, styles.projectSelectWrapper)}
    >
      {projectId !== null && (
        <span
          className={classNames(styles.clearButton, 'ignore-react-onclickoutside')}
          onClick={handleProjectClear}
        >
          <Tooltip position="top" title="Clear the project">
            <FontAwesome className={styles.clearButtonInner} name="times" />
          </Tooltip>
        </span>
      )}
      <ProjectSelect
        clients={clients}
        projects={projects}
        currencySymbol={currency.symbol}
        value={projectId}
        onChange={handleProjectChange}
        onSearch={handleProjectSearch}
        placeholder="Update project"
        placeholderColor="#6e848b"
        overlayClassName={styles.projectSelectOverlay}
        inputClassName={styles.projectSelectInput}
        loadingIndicatorClassName={styles.projectSelectLoadingIndicator}
        allProjectsLoaded={allProjectsLoaded}
        colorBoxIcon={null}
        disabled={disabled}
        isLoading={isLoading}
        size={size}
      />
    </div>
  );
};

export default ReportBatchUpdateProject;
